import {useEffect, useRef} from 'react';
import {Action} from '@sail/ui';
import {setValue} from 'src/v2/state/store';
import {WalletItem} from 'src/v2/components/wallet/WalletItem';
import {isMobile} from 'src/utils/utils';
import {COLORS} from 'src/v2/util/color';
import {logger} from 'src/utils/logger';
import type {PaymentOption} from 'src/v2/state/types';

interface Props {
  walletOptions: PaymentOption[];
  focusedIndex?: number;
  onFocusChange?: (index: number) => void;
}

export const WalletGrid = ({
  walletOptions,
  focusedIndex,
  onFocusChange,
}: Props) => {
  const itemRefs = useRef<(HTMLAnchorElement | null)[]>([]);

  useEffect(() => {
    // focusedIndex >= 0 means a grid item should be focused (not the search input)
    if (focusedIndex !== undefined && focusedIndex >= 0) {
      itemRefs.current[focusedIndex]?.focus();
    }
  }, [focusedIndex]);

  return (
    <>
      {walletOptions.map((walletOption, index) => (
        <Action
          ref={(el: HTMLAnchorElement | null) => {
            itemRefs.current[index] = el;
          }}
          key={
            walletOption.provider === 'WalletConnect' ||
            walletOption.provider === 'Exchange'
              ? walletOption.key
              : walletOption.provider
          }
          onPress={() => {
            const {provider} = walletOption;
            logger.info('selecting wallet', {
              provider,
              key:
                provider === 'WalletConnect' || provider === 'Exchange'
                  ? walletOption.key
                  : null,
              view: 'wallet_grid',
            });

            if (provider === 'Exchange') {
              setValue((state) => {
                state.exchangeOption = walletOption;
                state.screen = 'EXCHANGE';
              });
              return;
            }

            setValue((state) => {
              state.newWallet = {
                walletOption,
                networkType: provider === 'WalletConnect' ? 'evm' : null,
              };
              state.screen =
                provider === 'WalletConnect' ||
                ('isInstalledOnBrowser' in walletOption &&
                  walletOption.isInstalledOnBrowser) ||
                isMobile()
                  ? 'NETWORK_SELECT'
                  : 'INSTALL_WALLET';
            });
          }}
          css={{
            width: 'fill',
            borderRadius: 'small',
            ':hover': {
              backgroundColor: COLORS.offsetHoverOnLight,
            },
            ':focus-visible': {
              backgroundColor: COLORS.offsetHoverOnLight,
            },
            padding: '12px 4px 8px 4px',
          }}
          onFocus={() => onFocusChange?.(index)}
        >
          <WalletItem walletOption={walletOption} />
        </Action>
      ))}
    </>
  );
};
